/**
 * Owner-editable EMAIL wording registry (Owner console "Email templates" page).
 *
 * Mirrors the split modelPrompts.ts already uses for AI prompts: a code DEFAULT for the
 * editable part of each email, an optional DB override that replaces it, and a LOCKED part
 * (the CTA button, the OTP code box, the DPDP compliance list, the footer) that no override
 * can touch. `apps/worker/src/providers/email.ts` re-assembles the locked structure around
 * whatever subject/body wins here; `apps/worker/src/emailTemplates.ts` resolves the override.
 *
 * SIMPLIFICATION, DELIBERATE: pluralization ("1 candidate" vs "3 candidates") and date
 * formatting stay code-computed and are handed in as ready-to-use vars (`count`, `scored`,
 * `when`) rather than becoming template logic — an owner phrases around them (e.g.
 * "candidate(s)") instead of the registry supporting conditionals. One string-substitution
 * function, not a template language.
 */
/** One sample link, used both as a variable's documented sample and by a test send, so the
 *  preview a owner sees and the email a test actually produces cannot disagree. */
const SAMPLE_LINK = "https://app.talenthop.ai/i/sample-preview-token";
const VAR = {
    name: { key: "name", label: "Recipient's name", sample: "Priya Shah" },
    requisition: { key: "requisition", label: "Job requisition title", sample: "Senior Backend Engineer" },
    orgName: { key: "orgName", label: "Organisation name", sample: "Acme Corp" },
    count: { key: "count", label: "Number of candidates added", sample: "3" },
    scored: { key: "scored", label: "Number of candidates scored", sample: "3" },
    when: { key: "when", label: "Scheduled interview time", sample: "Sat, 12 Sep 2026 14:00:00 GMT" },
    candidate: { key: "candidate", label: "Candidate's name", sample: "Priya Shah" },
    email: { key: "email", label: "Applicant's email", sample: "priya@acme.example" },
    // ── candidate_info_request ────────────────────────────────────────────────
    // Named as specified for this template. The renderer matches {{\w+}}, so snake_case and the
    // camelCase used by the older templates both resolve — an owner sees the names documented on
    // the template they are editing, and nothing had to be renamed to add these.
    candidateNameVar: { key: "candidate_name", label: "Candidate's name", sample: "Priya Shah" },
    jobTitle: { key: "job_title", label: "Job title", sample: "Senior Backend Engineer" },
    companyName: { key: "company_name", label: "Your company name", sample: "Acme Corp" },
    candidateEmailVar: { key: "candidate_email", label: "Candidate's email", sample: "priya@example.com" },
    profileLink: {
        key: "profile_link",
        label: "Secure link to complete their profile",
        sample: SAMPLE_LINK,
    },
    resumeUploadLink: {
        key: "resume_upload_link",
        label: "Secure link to upload a resume (same page as the profile link)",
        sample: SAMPLE_LINK,
    },
    recruiterNote: {
        key: "recruiter_note",
        label: "Optional note the recruiter typed (empty when they left it blank)",
        sample: "We couldn't find a CV attached to your email.",
    },
};
export const EMAIL_TEMPLATES = [
    {
        key: "interview_link",
        label: "Interview invite",
        trigger: "A candidate is invited to complete an interview.",
        recipient: "Candidate",
        variables: [VAR.name, VAR.requisition],
        lockedNote: "A button linking to the interview is always added after this text.",
    },
    {
        key: "link_recovered",
        label: "Interview link resent",
        trigger: "A candidate asks to have their interview link resent.",
        recipient: "Candidate",
        variables: [VAR.name, VAR.requisition],
        lockedNote: "A button with the new interview link is always added after this text, along with a " +
            "security note that it replaces any earlier link.",
    },
    {
        key: "interview_scheduled",
        label: "Interview scheduled",
        trigger: "A candidate books an interview time slot.",
        recipient: "Candidate",
        variables: [VAR.name, VAR.requisition, VAR.when],
        lockedNote: "A button with the interview link is always added after this text.",
    },
    {
        key: "report_ready",
        label: "Report ready",
        trigger: "Scoring finishes for a requisition.",
        recipient: "Org admin",
        variables: [VAR.requisition, VAR.scored],
        lockedNote: "A button linking to the report is always added after this text.",
    },
    {
        key: "requisition_created",
        label: "Requisition created",
        trigger: "A new requisition is created (opt-in admin notification).",
        recipient: "Org admin (opted in)",
        variables: [VAR.requisition],
        lockedNote: "No additional locked content — this is the full email body.",
    },
    {
        key: "candidates_added",
        label: "Candidates added",
        trigger: "A batch of resumes is uploaded to a requisition (opt-in notification).",
        recipient: "Org admin (opted in)",
        variables: [VAR.requisition, VAR.count],
        lockedNote: "No additional locked content — this is the full email body.",
    },
    {
        key: "interview_completed",
        label: "Interview completed",
        trigger: "A candidate finishes their interview (opt-in notification).",
        recipient: "Org admin (opted in)",
        variables: [VAR.requisition, VAR.candidate],
        lockedNote: "No additional locked content — this is the full email body.",
    },
    {
        key: "report_shared",
        label: "Report shared",
        trigger: "An owner shares a report link with someone.",
        recipient: "Report recipient",
        variables: [VAR.requisition],
        lockedNote: "A button linking to the shared report is always added after this text.",
    },
    {
        key: "outcome",
        label: "Application outcome",
        trigger: "A hiring decision is recorded for a candidate.",
        recipient: "Candidate",
        variables: [VAR.name, VAR.requisition],
        lockedNote: "A button (when a feedback link is available) is always added after this text.",
    },
    {
        key: "vendor_assigned",
        label: "Vendor assigned",
        trigger: "A requisition is assigned to a vendor.",
        recipient: "Vendor",
        variables: [VAR.requisition],
        lockedNote: "A button linking to TalentHop is always added after this text.",
    },
    {
        key: "dsr_erase_request",
        label: "Data erasure request (DPDP)",
        trigger: "A candidate exercises their DPDP right to erasure.",
        recipient: "Platform owner/admin (internal compliance alert)",
        variables: [VAR.requisition],
        lockedNote: "A list of the candidate, organisation, requisition and reference IDs is always added " +
            "after this text — required for compliance handling and never removable.",
    },
    {
        key: "account_invite",
        label: "Account invite",
        trigger: "A new team member is added to an organisation.",
        recipient: "New team member",
        variables: [VAR.name, VAR.orgName],
        lockedNote: "A button to set a password is always added after this text, with a note that the link " +
            "is single-use and expires in 72 hours.",
    },
    {
        key: "verify_signup",
        label: "Verify signup email",
        trigger: "Self-serve signup — the new account's email needs verifying.",
        recipient: "New signup",
        variables: [VAR.name, VAR.orgName],
        lockedNote: "A button to verify the email is always added after this text, with a note that the link " +
            "is single-use and expires in 24 hours.",
    },
    {
        key: "signup_approved",
        label: "Signup approved",
        trigger: "An admin approves a pending signup request.",
        recipient: "New org owner",
        variables: [VAR.name, VAR.orgName],
        lockedNote: "A sign-in button is always added after this text.",
    },
    {
        key: "signup_rejected",
        label: "Signup not approved",
        trigger: "An admin declines a pending free-plan signup request.",
        recipient: "Applicant",
        variables: [VAR.name, VAR.orgName],
        lockedNote: "Nothing is added after this text.",
    },
    {
        key: "signup_review",
        label: "New signup to approve",
        trigger: "A free-plan signup has verified its email and is waiting for approval.",
        recipient: "Super Admin (SIGNUP_APPROVER_EMAILS)",
        variables: [VAR.name, VAR.orgName, VAR.email],
        lockedNote: "The applicant's details and an Approve button are always added after this text, with a " +
            "note that the link is single-use, works only for this signup and expires in 7 days.",
    },
    {
        key: "reset_password",
        label: "Password reset",
        trigger: "A user requests a password reset.",
        recipient: "User",
        variables: [VAR.name],
        lockedNote: "A password-reset button is always added after this text, with a note that the link " +
            "expires in 1 hour and is single-use.",
    },
    {
        key: "login_code",
        label: "Sign-in code",
        trigger: "Email-OTP sign-in.",
        recipient: "User",
        variables: [VAR.name],
        lockedNote: "A large sign-in code is always shown after this text; it expires in 10 minutes.",
    },
    {
        key: "verify_email",
        label: "Verify email (MFA)",
        trigger: "Email verification for account/MFA.",
        recipient: "User",
        variables: [VAR.name],
        lockedNote: "A large verification code is always shown after this text; it expires in 10 minutes.",
    },
    {
        key: "candidate_otp",
        label: "Candidate identity check-in code",
        trigger: "A candidate verifies their identity before an interview (OTP method).",
        recipient: "Candidate",
        variables: [VAR.name, VAR.requisition],
        lockedNote: "A large verification code is always shown after this text; it expires in 10 minutes.",
    },
    {
        key: "candidate_info_request",
        label: "Request candidate details & CV",
        trigger: "A recruiter asks a candidate for missing information — typically an application that " +
            "arrived without a resume attached.",
        recipient: "Candidate",
        variables: [
            VAR.candidateNameVar,
            VAR.jobTitle,
            VAR.companyName,
            VAR.candidateEmailVar,
            VAR.profileLink,
            VAR.resumeUploadLink,
            VAR.recruiterNote,
        ],
        lockedNote: "A \u201cComplete your profile\u201d button linking to the candidate's own secure page is always " +
            "added after this text, along with the link's expiry. The candidate does not need an " +
            "account to open it.",
    },
];
export const EMAIL_TEMPLATE_DEFAULTS = {
    candidate_info_request: {
        subject: "Please complete your application for {{job_title}}",
        body: "Hello {{candidate_name}},\n\nWe need some additional information to complete your " +
            "application for {{job_title}} at {{company_name}}. Please use the button below to provide " +
            "your details and upload your resume.",
    },
    interview_link: {
        subject: "Your TalentHop interview for {{requisition}}",
        body: "Hi {{name}},\n\nYou're invited to complete your interview for {{requisition}} with Asha, " +
            "your TalentHop interviewer. It takes about 15 minutes and you can do it whenever suits you.",
    },
    link_recovered: {
        subject: "Your interview link for {{requisition}}",
        body: "Hi {{name}},\n\nHere's your link to continue your interview for {{requisition}}. You can " +
            "pick up wherever you left off.",
    },
    interview_scheduled: {
        subject: "Interview confirmed — {{requisition}}",
        body: "Hi {{name}},\n\nYour interview for {{requisition}} is confirmed for {{when}}.",
    },
    report_ready: {
        subject: "Your TalentHop report is ready — {{requisition}}",
        body: "The assessment report for {{requisition}} is ready ({{scored}} candidate(s) scored). " +
            "Sign in to TalentHop to review the ranked shortlist and evidence.",
    },
    requisition_created: {
        subject: "New job request — {{requisition}}",
        body: "{{requisition}} was just created in your TalentHop workspace. Sign in to TalentHop to review it.",
    },
    candidates_added: {
        subject: "{{count}} new candidate(s) — {{requisition}}",
        body: "{{count}} new candidate(s) were added to {{requisition}}. Sign in to TalentHop to review them.",
    },
    interview_completed: {
        subject: "Interview completed — {{requisition}}",
        body: "{{candidate}} completed their interview for {{requisition}}. Scoring runs automatically " +
            "— you'll hear separately once it's ready.",
    },
    report_shared: {
        subject: "A TalentHop report was shared with you — {{requisition}}",
        body: "A hiring report for {{requisition}} has been shared with you.",
    },
    outcome: {
        subject: "Update on your application — {{requisition}}",
        body: "Hi {{name}},\n\nThere's an update on your application for {{requisition}}. Thank you for " +
            "the time you invested with us.",
    },
    vendor_assigned: {
        subject: "New TalentHop requisition assigned — {{requisition}}",
        body: "A requisition, {{requisition}}, has been assigned to you on TalentHop.",
    },
    dsr_erase_request: {
        subject: "Action needed — data erasure request ({{requisition}})",
        body: "A candidate has requested erasure of their data under the DPDP Act.\n\nThis is not " +
            "processed automatically. Please action it manually in the TalentHop compliance console.",
    },
    account_invite: {
        subject: "You're invited to TalentHop — {{orgName}}",
        body: "Hi {{name}},\n\nYou've been added to {{orgName}} on TalentHop. Set your password to " +
            "activate your account and sign in.",
    },
    verify_signup: {
        subject: "Verify your email to finish setting up {{orgName}}",
        body: "Hi {{name}},\n\nThanks for signing up for TalentHop for {{orgName}}. Verify your email " +
            "address to continue.",
    },
    signup_approved: {
        subject: "You're approved — welcome to TalentHop",
        body: "Hi {{name}},\n\n{{orgName}} is ready. Sign in with the email and password you set at " +
            "signup to get started.",
    },
    signup_rejected: {
        subject: "About your TalentHop signup",
        body: "Hi {{name}},\n\nThanks for your interest in TalentHop. We weren't able to approve the free " +
            "plan for {{orgName}} this time — it is meant for companies hiring with a work email.\n\n" +
            "If you think this is a mistake, just reply to this email and we'll take another look. " +
            "Paid plans are available any time from our pricing page.",
    },
    signup_review: {
        subject: "New signup to approve — {{orgName}}",
        body: "A new user has signed up on TalentHop and verified their email.\n\nPlease review the " +
            "details below and approve the account.",
    },
    reset_password: {
        subject: "Reset your TalentHop password",
        body: "Hi {{name}},\n\nWe received a request to reset your TalentHop password. Choose a new one " +
            "using the button below.",
    },
    login_code: {
        subject: "Your TalentHop sign-in code",
        body: "Hi {{name}},\n\nYour sign-in code is:",
    },
    verify_email: {
        subject: "Verify your email — TalentHop",
        body: "Hi {{name}},\n\nUse this code to verify your email address:",
    },
    candidate_otp: {
        subject: "Your TalentHop interview verification code",
        body: "Hi {{name}},\n\nUse this code to verify your identity before your interview for {{requisition}}:",
    },
};
export const EMAIL_TEMPLATE_KEYS = EMAIL_TEMPLATES.map((t) => t.key);
export const EMAIL_TEMPLATE_SUBJECT_MAX_LEN = 200;
export const EMAIL_TEMPLATE_BODY_MAX_LEN = 4000;
/** An HTML body carries its own markup, so it needs far more room than the plain-text one.
 *  Matches the CHECK on email_template_overrides.body_html_override (migration 0085). */
export const EMAIL_HTML_MAX_LEN = 20000;
/** Every variable available for a template, keyed for a live preview (sample values, not real data). */
export function sampleVarsForTemplate(key) {
    const meta = EMAIL_TEMPLATES.find((t) => t.key === key);
    const out = {};
    for (const v of meta?.variables ?? [])
        out[v.key] = v.sample;
    return out;
}
/**
 * Full sample vars for an actual TEST SEND — the editable {{tokens}} above PLUS the vars that
 * only the LOCKED structural section reads (the CTA link, an OTP code, the scheduled slot a
 * date gets formatted from, the DSR compliance list's reference IDs). `sampleVarsForTemplate`
 * only covers the editable half, which is enough for the client-side text preview; a real send
 * needs the rest too, or the button/code box a real recipient would see renders empty. Every
 * value here is a placeholder — never real candidate, org, or account data.
 */
const SAMPLE_CODE = "482913";
// 2026-09-12T14:00:00.000Z, formatted via toUTCString() the way the worker computes `when` from
// `slot` — must stay equal to VAR.when.sample so the client preview (direct {{when}} token) and a
// real test send (worker recomputes `when` from `slot`, ignoring any raw `when` here) agree.
const SAMPLE_SLOT = "2026-09-12T14:00:00.000Z";
const SAMPLE_PERSONA_REF = "sample-persona-ref";
const SAMPLE_CANDIDACY_REF = "sample-candidacy-ref";
export const EMAIL_TEMPLATE_TEST_VARS = {
    candidate_info_request: {
        candidate_name: VAR.candidateNameVar.sample,
        job_title: VAR.jobTitle.sample,
        company_name: VAR.companyName.sample,
        candidate_email: VAR.candidateEmailVar.sample,
        profile_link: SAMPLE_LINK,
        resume_upload_link: SAMPLE_LINK,
        recruiter_note: VAR.recruiterNote.sample,
        // `link` is what the worker's locked CTA button reads for every template.
        link: SAMPLE_LINK,
    },
    interview_link: { name: VAR.name.sample, requisition: VAR.requisition.sample, link: SAMPLE_LINK },
    link_recovered: { name: VAR.name.sample, requisition: VAR.requisition.sample, link: SAMPLE_LINK },
    interview_scheduled: {
        name: VAR.name.sample,
        requisition: VAR.requisition.sample,
        // `slot` is what the worker actually reads (it derives `when` from this); `when` is included
        // too so a direct {{when}} substitution — outside the worker's own render — still resolves.
        slot: SAMPLE_SLOT,
        when: VAR.when.sample,
        link: SAMPLE_LINK,
    },
    report_ready: { requisition: VAR.requisition.sample, scored: VAR.scored.sample, link: SAMPLE_LINK },
    requisition_created: { requisition: VAR.requisition.sample },
    candidates_added: { requisition: VAR.requisition.sample, count: VAR.count.sample },
    interview_completed: { requisition: VAR.requisition.sample, candidate: VAR.candidate.sample },
    report_shared: { requisition: VAR.requisition.sample, link: SAMPLE_LINK },
    outcome: { name: VAR.name.sample, requisition: VAR.requisition.sample, link: SAMPLE_LINK },
    vendor_assigned: { requisition: VAR.requisition.sample, link: SAMPLE_LINK },
    dsr_erase_request: {
        name: VAR.name.sample,
        requisition: VAR.requisition.sample,
        orgName: VAR.orgName.sample,
        personaId: SAMPLE_PERSONA_REF,
        candidacyId: SAMPLE_CANDIDACY_REF,
    },
    account_invite: { name: VAR.name.sample, orgName: VAR.orgName.sample, link: SAMPLE_LINK },
    verify_signup: { name: VAR.name.sample, orgName: VAR.orgName.sample, link: SAMPLE_LINK },
    signup_approved: { name: VAR.name.sample, orgName: VAR.orgName.sample, link: SAMPLE_LINK },
    signup_rejected: { name: VAR.name.sample, orgName: VAR.orgName.sample },
    signup_review: {
        name: VAR.name.sample,
        orgName: VAR.orgName.sample,
        email: VAR.email.sample,
        plan: "Free",
        link: SAMPLE_LINK,
    },
    reset_password: { name: VAR.name.sample, link: SAMPLE_LINK },
    login_code: { name: VAR.name.sample, code: SAMPLE_CODE },
    verify_email: { name: VAR.name.sample, code: SAMPLE_CODE },
    candidate_otp: { name: VAR.name.sample, requisition: VAR.requisition.sample, code: SAMPLE_CODE },
};
function escapeHtmlValue(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
/**
 * Substitute {{key}} tokens with values from `vars`. `escapeHtml` HTML-escapes the substituted
 * values (for the HTML body); pass false for the subject header and the plain-text body, which
 * carry no markup. A token with no matching key in `vars` is left in the string untouched —
 * visibly wrong beats silently vanishing.
 */
export function renderEmailTokens(template, vars, escapeHtml) {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        if (!(key in vars))
            return match;
        const value = vars[key] ?? "";
        return escapeHtml ? escapeHtmlValue(value) : value;
    });
}
// ── enable / disable ─────────────────────────────────────────────────────────
/**
 * Templates that may NOT be switched off.
 *
 * Disabling an email is a legitimate product choice for a notification — an org that does not
 * want "candidates added" digests should be able to stop them. It is NOT a legitimate choice for
 * a message someone is waiting on to get into their account or to prove who they are: switching
 * off `login_code` locks every OTP user out of the platform, and switching off `reset_password`
 * strands anyone who has forgotten theirs, with no error anywhere to explain why.
 *
 * So the switch exists, and these are excluded from it. The API refuses a disable on one of
 * these rather than storing a setting that would break sign-in.
 */
export const UNDISABLEABLE_EMAIL_TEMPLATES = [
    "login_code",
    "reset_password",
    "verify_email",
    "verify_signup",
    "signup_review",
    "candidate_otp",
    "account_invite",
];
export const canDisableEmailTemplate = (key) => !UNDISABLEABLE_EMAIL_TEMPLATES.includes(key);
// ── HTML body safety ─────────────────────────────────────────────────────────
/**
 * Tags an owner-authored HTML body may use. Deliberately small: this is email HTML, where most
 * of the modern web is unsupported by mail clients anyway, and every tag left out is one fewer
 * way to produce a message that renders as raw markup in Outlook.
 */
export const ALLOWED_EMAIL_HTML_TAGS = [
    "p", "br", "b", "strong", "i", "em", "u", "a", "ul", "ol", "li",
    "h1", "h2", "h3", "h4", "span", "div", "table", "tr", "td", "th",
    "tbody", "thead", "hr", "blockquote", "img",
];
/**
 * Check an owner-authored HTML email body for things that must not be stored.
 *
 * WHY THIS EXISTS EVEN THOUGH ONLY AN OWNER CAN WRITE HERE. The output is not rendered in our
 * app — it is mailed to candidates and opened in their mail client, and it is stored once and
 * sent to everyone who ever receives that template. A script tag or an `onerror` attribute that
 * reaches this column is a payload with a distribution list. Trusting the author is not the same
 * as making the stored artefact safe, and the cost of being wrong lands on the recipient.
 *
 * Returns [] when the HTML is acceptable. Conservative by design: it rejects rather than strips,
 * so an owner is told what is wrong instead of silently getting different markup than they wrote.
 */
export function validateEmailHtml(html) {
    const problems = [];
    const lower = html.toLowerCase();
    if (/<\s*(script|style|iframe|object|embed|form|link|meta|base)\b/.test(lower)) {
        problems.push({
            message: "Scripts, styles, iframes, forms and embeds aren't allowed in an email body — most mail " +
                "clients strip them anyway, and they can't be stored here.",
        });
    }
    // Inline event handlers (onclick, onerror, onload…).
    if (/\bon[a-z]+\s*=/.test(lower)) {
        problems.push({ message: "Inline event handlers (onclick, onerror, …) aren't allowed." });
    }
    // javascript:/data: URLs in href/src.
    if (/(href|src)\s*=\s*["']?\s*(javascript|data|vbscript):/.test(lower)) {
        problems.push({ message: "Links and images must use http(s) URLs." });
    }
    // Any tag outside the allow-list.
    const allowed = new Set(ALLOWED_EMAIL_HTML_TAGS);
    for (const m of lower.matchAll(/<\s*\/?\s*([a-z][a-z0-9]*)/g)) {
        const tag = m[1];
        if (!allowed.has(tag)) {
            problems.push({ message: `<${tag}> isn't supported in an email body.` });
            break; // one message is enough — listing every instance helps nobody
        }
    }
    return problems;
}
//# sourceMappingURL=emailTemplates.js.map